import { useEffect, useMemo, useRef, useState } from 'react'
import Select, { components, MenuProps, MultiValue } from 'react-select'
import {
    DropDownOption,
    nodeConfigurations,
} from '../survey/SurveyModel/types/nodeTypes'
import Errors from './Error'

const MultiSelect = (props: any) => {
    const {
        options,
        name,
        form,
        handleChange,
        value,
        propDivClasses = '',
        isInValid = false,
        onBlur,
        disabled,
        configurations,
        placeholder,
        shouldSelectAllDefault = true,
    } = props
    const [optionList, setOptionList] = useState<DropDownOption[]>(options)
    const [isFocused, setIsFocused] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current?.contains(event.target as Node)) {
                setIsActive(false);
                handleOnBlur();
            }
        }
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!shouldSelectAllDefault && (!value || value.length === 0)) {
            handleChange([]) // Explicitly set empty selection
        } else if (shouldSelectAllDefault && optionList?.length > 0) {
            handleChange(optionList)
        }
    }, [optionList, shouldSelectAllDefault])

    const handleOnBlur = () => {
        setIsFocused(false)
        setIsActive(false)
        if (onBlur) {
            onBlur()
        }
    }

    useEffect(() => {
        if (options?.length) {
            setOptionList(options)
        }
    }, [options])

    const formatOptionLabel = (
        { label, description }: DropDownOption,
        { context }: any
    ) => {
        return context === 'menu' ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div>{label}</div>
                <div>{description}</div>
            </div>
        ) : (
            <div>{label}</div>
        )
    }

    // Custom Option with Checkbox
    const CheckboxOption = (props: any) => {
        return (
            <components.Option {...props}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        
                    }}
                >
                    <input
                        type="checkbox"
                        checked={value?.includes(props.label)}
                        onClick={() => {}}
                        style={{
                             width: 0,
                             height: 0,
                            
                            
                        }}
                    />
                    <svg width="18" height="18" viewBox="0 0 18 18">
                        <rect
                            x="0"
                            y="0"
                            width="18"
                            height="18"
                            rx="0"
                            ry="0"
                            fill={props.isSelected ? 'white' : 'white'}
                            stroke="black"
                            strokeWidth="1"
                        />
                        {props.isSelected && (
                            <polyline
                                points="4.5,9 7.5,12.5 13.5,5.5"
                                fill="none"
                                stroke="#3F3F3F"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        )}
                    </svg>

                    
                    <label style={{ marginLeft:'8px' , fontSize:'14px' , fontWeight:'400'}}>{props.label}</label>
                </div>
            </components.Option>
        )
    }

    const CustomValueContainer = useMemo(() => ({ children, ...props }: any) => {
        const { hasValue, selectProps } = props
        const { value, options, placeholder } = selectProps

        const currentValue = Array.isArray(value) ? value : []
        const allSelected = currentValue?.length === options?.length && options?.length > 0

        const selectedLabels = currentValue
            ?.filter((item: any) => item?.value !== '0')
            ?.map((item: any) => item?.label)
            ?.filter(Boolean)

        if (allSelected) {
            return (
                <components.ValueContainer {...props}>
                    <div
                        style={{
                            paddingLeft: '2px',
                            color: 'var(--comp-field-fontcolour-input-default)',
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default)',
                            pointerEvents: 'none',
                        }}
                    >
                        {placeholder}
                        { children }
                    </div>
                </components.ValueContainer>
            )
        }

        return (
            <components.ValueContainer {...props}>
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        minWidth: 0,
                        color: 'var(--comp-field-fontcolour-input-default)',
                        fontSize: 'var(--comp-field-fontsize-input-default)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        alignItems: 'center',
                        pointerEvents: 'none',
                    }}
                    title={selectedLabels?.join(', ')}
                >
                    {hasValue && selectedLabels?.length > 0
                            ? selectedLabels.join(', ')
                            : undefined}
                    {children}
                </div>
                
            </components.ValueContainer>
        )
    }, [value, options, placeholder])

    // In your Select component styles:
    const selectStyles = {
        control: (base: any) => ({
            ...base,
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
        }),
        valueContainer: (base: any) => ({
            ...base,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            paddingRight: '30px',

            flex: 1,
            minWidth: 0,
        }),
    }

    ;<Select
        components={{
            Option: CheckboxOption,
            ValueContainer: CustomValueContainer,
            MultiValueRemove: () => null,
            IndicatorSeparator: () => null,
            // Hide the multi-value items completely
            MultiValue: () => null,
        }}
        styles={selectStyles}
    />

    const handleCheckboxChange = (
        selectedOptions: MultiValue<DropDownOption>
    ) => {
        const allOption = options.find(
            (opt: DropDownOption) => opt.value === '0'
        )
        const regularOptions = options.filter(
            (opt: DropDownOption) => opt.value !== '0'
        )

        const selected = Array.isArray(selectedOptions) ? selectedOptions : []
        if (
            selected.some((opt: DropDownOption) => opt.value === '0') &&
            !value?.some((opt: DropDownOption) => opt.value === '0')
        ) {
            handleChange(options.filter((opt: DropDownOption) => opt.value !== -1))
            return
        }
        if (
            !selected.some((opt: DropDownOption) => opt.value === '0') &&
            value?.some((opt: DropDownOption) => opt.value === '0')
        ) {
            handleChange([])
            return
        }
        if (!selected.some((opt: DropDownOption) => opt.value === '0')) {
            if (
                regularOptions.filter((opt: DropDownOption) => opt.value !== -1).every((opt: DropDownOption) =>
                    selected.some((s: DropDownOption) => s.value === opt.value)
                )
            ) {
                if (allOption) {
                    handleChange([...selected, allOption])
                } else {
                    handleChange(selected)
                }
                return
            }

            handleChange(selected)
            return
        }
        if (
            selected.some((opt: DropDownOption) => opt.value === '0') &&
            value?.some((opt: DropDownOption) => opt.value === '0')
        ) {
            let newSelection = [];
            const hasNoOption = options.some((opt: DropDownOption) => opt.value === -1);
            if (hasNoOption) {
                if (selected.length < value.length && selected.some((opt: DropDownOption) => opt.value == -1)) {
                    newSelection = selected.filter(
                                    (opt: DropDownOption) => opt.value !== '0'
                )
                } else {
                    newSelection = selected;
                }
            } else {
                newSelection = selected.filter(
                (opt: DropDownOption) => opt.value !== '0'
                )
            }
            handleChange(newSelection)
            return
        }
        handleChange(selected)
    }

    const CustomMenu = useMemo(() => (props: MenuProps<DropDownOption>) => {
        return (
            <components.Menu {...props}>
                <div>
                    {props.children}
                </div>
            </components.Menu>
        )
    }, [])

    return (
        <div ref={wrapperRef} className={`${isInValid ? 'error' : ''}`}>
            <div
                className={`${propDivClasses} input-div ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'width-half' : ''} ${isFocused ? 'input-div-focus' : ''} ${isActive ? 'input-div-active' : 'input-div-inactive'}`}
            >
                <Select
                    className={`
              custom-select
              ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'custom-select-lg' : ''}
              ${disabled ? 'disabled' : ''}`}
                    blurInputOnSelect={false}
                    closeMenuOnSelect={false}
                    name={name}
                    placeholder={disabled ? '' : placeholder}
                    options={optionList}
                    onBlur={handleOnBlur}
                    onChange={handleCheckboxChange}
                    value={Array.isArray(value) ? value : []}
                    onMenuOpen={() => setIsActive(true)}
                    onMenuClose={() => setIsActive(false)}
                    isDisabled={disabled}
                    hideSelectedOptions={false}
                    isMulti
                    isSearchable={false}
                    menuIsOpen={isActive}
                    components={{
                        Option: CheckboxOption,
                        ValueContainer: CustomValueContainer,
                        MultiValueRemove: () => null,
                        ClearIndicator: () => null,
                        IndicatorSeparator: () => null,
                        MultiValue: () => null,
                        Menu: CustomMenu,
                    }}
                    formatOptionLabel={formatOptionLabel}
                    styles={{
                        control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused
                                ? 'var(--comp-field-bordercolor-default)'
                                : 'var(--comp-field-bordercolor-default)',
                            borderRadius: '0',
                            boxShadow: 'none',
                            ':hover': {
                                borderColor:
                                    'var(--comp-field-bordercolor-default)',
                            },
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default)',
                            color: 'var(--comp-field-fontcolour-input-default))',
                            fontFamily: '$Lexend',
                            height: 'var(--input-height)',
                            minHeight: 'var(--input-height)',
                            minWidth: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                        }),
                        dropdownIndicator: (provided: any, state: any) => ({
                            ...provided,
                            color: state.isFocused
                                ? 'var(--comp-field-fontcolour-input-default))'
                                : 'var(--comp-icon-color-positive)',
                            ':hover': {
                                color: 'darkblue',
                            },
                            display: disabled ? 'none' : 'flex',
                        }),
                        menu: (provided: any) => ({
                            ...provided,
                            borderRadius: 0,
                            margin: 0,
                            padding: '0px 0px',
                            width: `calc(100% + 4px)`,
                            left: '-2px',
                            borderTop: `${isInValid ? '3px solid var(--comp-field-bordercolor-error' : '3px solid var(--comp-field-bordercolor-focus'})`,
                        }),
                        menuList: (provided) => ({
                            ...provided,
                            maxHeight: '200px',
                            height: 'auto',
                            paddingTop: '0px',
                            paddingBottom: '0px',
                        }),
                        valueContainer: (baseStyles) => ({
                            ...baseStyles,
                            height: '36px',
                            display: 'flex',
                            flexWrap: 'nowrap',
                            overflow: 'hidden',
                            paddingRight: '30px',
                        }),
                        singleValue: (baseStyles) => ({
                            ...baseStyles,
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default',
                            color: 'var(--comp-field-fontcolour-input-default))',
                        }),
                        multiValue: (base) => ({
                            ...base,
                            display: 'none', // Hide the chips completely
                        }),
                        indicatorsContainer: (base) => ({
                            ...base,
                            flexShrink: 0,
                        }),
                        placeholder: (baseStyles) => ({
                            ...baseStyles,
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default',
                            color: 'var(--comp-field-fontcolour-input-default))',
                        }),
                        option: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isSelected
                                ? state.isFocused
                                    ? '#fff'
                                    : 'var(--comp-field-fontcolour-input-default)'
                                : state.isFocused
                                  ? '#fff'
                                  : 'var(--comp-field-fontcolour-input-default)',
                            backgroundColor: state.isFocused
                                ? 'var(--comp-field-bgcolor-menuoptionactive)'
                                : 'var(--comp-field-fontcolor-menu-active)',
                            fontSize: '14px',
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            paddingTop: '4px',
                            paddingBottom: '4px',

                        }),
                    }}
                />
            </div>
            {form ? (
                <Errors
                    fieldName={name}
                    validation={form[name] ? form[name] : undefined}
                />
            ) : (
                ''
            )}
        </div>
    )
}
export default MultiSelect